import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Button } from '@monksflow/monks-ui';
import { logger } from '@/lib/utils/logger';
import type { ErrorBoundaryProps } from './ErrorBoundary.types';
import { useTranslation } from 'react-i18next';

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return null;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation('chats');

  const errorMessage = getErrorMessage(error);

  const handleReset = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <div
      className="flex h-screen flex-col items-center justify-center p-8 text-center"
      role="alert"
    >
      <h2 className="text-heading-xl mb-3">{t('sessions.errorBoundary.oopsSomethingWentWrong')}</h2>
      <p className="text-body-lg text-muted-foreground mb-6">
        {t('sessions.errorBoundary.weEncounteredAnUnexpectedError')}
      </p>
      {errorMessage && (
        <p className="text-body-md text-destructive bg-muted mb-6 max-w-150 overflow-auto rounded-sm p-4 font-mono">
          {errorMessage}
        </p>
      )}
      <Button onClick={handleReset}>{t('sessions.errorBoundary.reloadPage')}</Button>
    </div>
  );
}

function handleError(error: Error, info: React.ErrorInfo) {
  logger.error('ErrorBoundary caught an error:', error, info);
}

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={fallback ? () => <>{fallback}</> : ErrorFallback}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
}
